import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, useSeo } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Star, Check, Minus, ArrowRight, Trophy, X } from "lucide-react";

function StorePicker({ label, value, onChange, stores, exclude, testId }) {
  return (
    <div>
      <label className="overline text-slate-500 block mb-3">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm bg-white focus:border-slate-950 focus:outline-none"
        data-testid={testId}
      >
        <option value="">Pick a store…</option>
        {stores.filter((s) => s.slug !== exclude).map((s) => (
          <option key={s.slug} value={s.slug}>{s.name}</option>
        ))}
      </select>
    </div>
  );
}

function StatRow({ label, aVal, bVal, verdict, format }) {
  const fmt = (v) => (format ? format(v) : (typeof v === "number" ? v.toLocaleString() : v));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-4 py-5 border-b border-slate-200 items-center" data-testid={`stat-row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="md:col-span-3 flex items-center gap-3">
        <span className={`text-2xl font-display tracking-tighter ${verdict === "a" ? "text-emerald-600" : "text-slate-950"}`}>
          {fmt(aVal)}
        </span>
        {verdict === "a" && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
      </div>
      <div className="md:col-span-1 text-center">
        <p className="overline text-slate-500">{label}</p>
      </div>
      <div className="md:col-span-3 flex items-center gap-3 md:justify-end md:flex-row-reverse">
        <span className={`text-2xl font-display tracking-tighter ${verdict === "b" ? "text-emerald-600" : "text-slate-950"}`}>
          {fmt(bVal)}
        </span>
        {verdict === "b" && <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />}
      </div>
    </div>
  );
}

function StoreColumn({ snap, align = "left", label }) {
  const s = snap.store;
  const [logoFailed, setLogoFailed] = useState(false);
  return (
    <div className={`flex-1 p-6 border border-slate-200 rounded-sm bg-white ${align === "right" ? "md:text-right" : ""}`}>
      <p className="overline text-slate-500 mb-3">{label}</p>
      <div data-testid={`compare-logo-tile-${s.slug}`} className={`w-20 h-20 border border-slate-200 flex items-center justify-center p-3 rounded-sm ${align === "right" ? "md:ml-auto" : ""}`}>
        {s.logo_url && !logoFailed ? (
          <img data-testid={`compare-logo-image-${s.slug}`} src={s.logo_url} alt={`${s.name} store logo`} className="max-w-full max-h-full object-contain"
               onError={() => setLogoFailed(true)} />
        ) : (
          <span data-testid={`compare-logo-fallback-${s.slug}`} className="font-display text-3xl">{s.name[0]}</span>
        )}
      </div>
      <h3 className="font-display text-3xl md:text-4xl tracking-tighter uppercase mt-4">{s.name}</h3>
      {snap.category && (
        <p className="overline text-slate-500 mt-2">{snap.category.name}</p>
      )}
      <p className="text-sm text-slate-600 mt-4 leading-relaxed line-clamp-3">{s.description}</p>
      <Link to={`/store/${s.slug}`} className={`inline-flex items-center gap-2 mt-6 text-sm font-medium link-underline ${align === "right" ? "md:flex-row-reverse" : ""}`}>
        Visit store page <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export default function Compare() {
  useSeo(
    "Compare Stores Side-by-Side — SharePromos",
    "Compare any two stores head-to-head on coupon count, shopper ratings, success rate, and popularity. Find the best deal at a glance.",
    {
      keywords: "compare stores, store comparison, best coupon site, retailer vs retailer, coupon showdown, deal comparison",
    }
  );
  const [stores, setStores] = useState([]);
  const [params, setParams] = useSearchParams();
  const [a, setA] = useState(params.get("a") || "");
  const [b, setB] = useState(params.get("b") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/stores", { params: { limit: 500 } }).then((r) => {
      const sorted = [...r.data].sort((x, y) => x.name.localeCompare(y.name));
      setStores(sorted);
    });
  }, []);

  useEffect(() => {
    if (!a || !b) { setData(null); return; }
    setErr("");
    api.get("/compare", { params: { a, b } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not compare"));
    setParams({ a, b });
  }, [a, b, setParams]);

  const swap = () => { const na = b; const nb = a; setA(na); setB(nb); };
  const clear = () => { setA(""); setB(""); setData(null); setParams({}); };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="compare-page">
      <Breadcrumbs items={[{ label: "Compare" }]} />
      <p className="overline mb-4">Side by side</p>
      <h1 className="font-display text-4xl md:text-6xl tracking-tighter uppercase mb-4">Store Comparison</h1>
      <p className="text-slate-600 max-w-2xl mb-10 leading-relaxed">
        Pit two retailers head-to-head. We&apos;ll show you who has more active coupons, higher shopper ratings, and the better track record at checkout.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-end mb-12 p-6 border border-slate-200 rounded-sm bg-slate-50">
        <StorePicker label="Store A" value={a} onChange={setA} stores={stores} exclude={b} testId="compare-select-a" />
        <div className="flex md:pb-1 gap-2">
          <button onClick={swap} disabled={!a || !b} data-testid="compare-swap"
                  className="px-4 py-3 border border-slate-300 rounded-sm text-sm hover:bg-slate-100 disabled:opacity-40">
            ⇄
          </button>
          <button onClick={clear} disabled={!a && !b} data-testid="compare-clear"
                  className="px-4 py-3 border border-slate-300 rounded-sm text-sm hover:bg-slate-100 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>
        <StorePicker label="Store B" value={b} onChange={setB} stores={stores} exclude={a} testId="compare-select-b" />
      </div>

      {err && <p className="text-red-600 text-sm mb-6" data-testid="compare-error">{err}</p>}

      {!data && !err && (
        <div className="text-center py-24 border border-dashed border-slate-300 rounded-sm" data-testid="compare-empty">
          <Minus className="w-10 h-10 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Choose two stores above to see the showdown.</p>
        </div>
      )}

      {data && (
        <>
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <StoreColumn snap={data.a} align="left" label="Store A" />
            <div className="flex items-center justify-center px-4">
              <div className="w-14 h-14 rounded-full border-2 border-slate-950 bg-white flex items-center justify-center font-display text-lg -my-6 md:my-0">
                VS
              </div>
            </div>
            <StoreColumn snap={data.b} align="right" label="Store B" />
          </div>

          {/* Overall verdict */}
          <div className="p-6 border border-slate-200 rounded-sm bg-slate-950 text-slate-100 mb-10 flex items-center gap-5" data-testid="compare-verdict">
            <Trophy className="w-10 h-10 text-amber-400 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="overline text-emerald-400 mb-2">Verdict</p>
              <p className="font-display text-2xl md:text-3xl tracking-tight">
                {data.overall_winner === "tie"
                  ? "It's a dead heat — both stores are neck and neck."
                  : `${(data.overall_winner === "a" ? data.a : data.b).store.name} wins overall.`}
              </p>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm p-6 md:p-10 bg-white">
            <StatRow label="Active coupons" aVal={data.a.coupon_count} bVal={data.b.coupon_count} verdict={data.verdict.coupon_count} />
            <StatRow
              label="Rating"
              aVal={data.a.rating}
              bVal={data.b.rating}
              verdict={data.verdict.rating}
              format={(v) => (
                <span className="inline-flex items-center gap-2">
                  {v.toFixed(1)}
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                </span>
              )}
            />
            <StatRow label="Reviews" aVal={data.a.review_count} bVal={data.b.review_count} verdict={data.verdict.review_count} />
            <StatRow label="Success rate" aVal={data.a.avg_success_rate} bVal={data.b.avg_success_rate} verdict={data.verdict.avg_success_rate} format={(v) => `${v}%`} />
            <StatRow label="Total uses" aVal={data.a.total_uses} bVal={data.b.total_uses} verdict={data.verdict.total_uses} />
          </div>

          {(data.a.best_coupon || data.b.best_coupon) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {[data.a, data.b].map((snap) => (
                <div key={snap.store.slug} className="border border-slate-200 p-6 rounded-sm">
                  <p className="overline mb-2">Top deal · {snap.store.name}</p>
                  {snap.best_coupon ? (
                    <>
                      <p className="font-display text-2xl tracking-tight mb-2">{snap.best_coupon.discount_label}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{snap.best_coupon.title}</p>
                      <p className="text-xs text-slate-400 mt-3">{snap.best_coupon.uses.toLocaleString()} shoppers used this</p>
                    </>
                  ) : (
                    <p className="text-slate-500 text-sm">No active coupons.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
